import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useChartTheme } from '../hooks/useChartTheme';
import {
  Car, Plus, Trash2, Search, Download, AlertTriangle, Fuel, Gauge,
  Calendar, DollarSign, Edit, CheckCircle, X as XIcon, TrendingUp, Upload, History, Save, FileUp, Link,
  ArrowUp, ArrowDown, ArrowUpDown, FileText, Paperclip
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import api, { lookupsAPI } from '../lib/api';
import { settle } from '../lib/useResilientLoad';
import AccessState from '../components/auth/AccessState';

// Clickable column header. Blanks always sort last so an empty VIN never
// outranks a real one.
function FleetSortHeader({ label, colKey, sortKey, sortDir, onSort, className = '' }) {
  const active = sortKey === colKey;
  return (
    <TableHead
      onClick={() => onSort(colKey)}
      data-testid={`fleet-sort-${colKey}`}
      className={`cursor-pointer select-none hover:text-slate-900 transition-colors ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active
          ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)
          : <ArrowUpDown className="w-3 h-3 opacity-30" />}
      </span>
    </TableHead>
  );
}

function sortRows(rows, sortKey, sortDir, accessors = {}) {
  if (!sortKey) return rows;
  const get = accessors[sortKey] || ((r) => r[sortKey]);
  const blank = (v) => v === null || v === undefined || v === '';
  return [...rows].sort((a, b) => {
    const av = get(a), bv = get(b);
    if (blank(av) && blank(bv)) return 0;
    if (blank(av)) return 1;
    if (blank(bv)) return -1;
    const cmp = (typeof av === 'number' && typeof bv === 'number')
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? cmp : -cmp;
  });
}

function useTableSort(initialKey = null, initialDir = 'asc') {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);
  const onSort = (key) => {
    if (key === sortKey) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); return; }
    setSortKey(key);
    setSortDir('asc');
  };
  return { sortKey, sortDir, onSort };
}

const DOC_TYPES = ['insurance', 'registration', 'title', 'warranty', 'inspection', 'other'];

export default function Fleet() {
  const chart = useChartTheme();
  const { hasRole } = useAuth();
  const perms = useEffectivePermissions();
  // Engine-aware write gate. View-only HR overrides should NOT see write actions.
  const canAdd = perms.can('fleet', 'add') || hasRole('admin', 'fleet_manager');
  const canEdit = perms.can('fleet', 'edit') || hasRole('admin', 'fleet_manager');
  const canDelete = perms.can('fleet', 'delete') || hasRole('admin', 'fleet_manager');
  const canExport = perms.can('fleet', 'export') || hasRole('admin', 'fleet_manager');
  const round = (num, dec) => Math.round(num * Math.pow(10, dec)) / Math.pow(10, dec);
  const [vehicles, setVehicles] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [expirationAlerts, setExpirationAlerts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Column sorting — one independent sort per table
  const vehicleSort = useTableSort('vehicle');
  const mileageSort = useTableSort('vehicle_name');
  const wexSort = useTableSort('date');
  const costSort = useTableSort('cost_per_mile', 'desc');

  // Cost per mile / TCO
  const [costs, setCosts] = useState(null);
  const [costMonths, setCostMonths] = useState(12);

  // Vehicle documents
  const [vehicleDocs, setVehicleDocs] = useState([]);
  const [docForm, setDocForm] = useState({ doc_type: 'insurance', expiration_date: '' });
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Dialogs
  const [showVehicle, setShowVehicle] = useState(false);
  const [showFuel, setShowFuel] = useState(false);
  const [showMileage, setShowMileage] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleMileage, setVehicleMileage] = useState([]);
  const [vehicleFuel, setVehicleFuel] = useState([]);
  const [fuelChartData, setFuelChartData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showMileageAudit, setShowMileageAudit] = useState(false);
  const [mileageAuditLog, setMileageAuditLog] = useState([]);
  const [bulkMileage, setBulkMileage] = useState(null);
  const [bulkEntries, setBulkEntries] = useState({});
  const [bulkMonth, setBulkMonth] = useState(new Date().getMonth() + 1);
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear());

  // WEX Import
  const [wexImports, setWexImports] = useState([]);
  const [wexUnmatched, setWexUnmatched] = useState([]);
  const [importing, setImporting] = useState(false);
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(null);

  // Forms
  const emptyVehicle = { make: '', model: '', year: '', color: '', vin: '', tag_number: '', tag_expiration: '', tag_state: '', assigned_to_employee_id: '', assigned_to_location_id: '', purpose: '', manager_contact: '', gas_card_number: '', warranty_details: '', warranty_expiration: '', notes: '', payment_due_date: '', date_opened: '', maturity_date: '', current_balance: '', payment_amount: '', interest_rate: '', max_gallons_threshold: 30 };
  const [vehicleForm, setVehicleForm] = useState({ ...emptyVehicle });
  const [fuelForm, setFuelForm] = useState({ vehicle_id: '', date: '', gallons: '', cost_per_gallon: '', total_cost: '', odometer: '', location: '', notes: '' });
  const [mileageForm, setMileageForm] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear(), total_mileage: '' });

  // Reference data
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  // Track whether secondary lookups were denied so dropdowns can degrade
  // gracefully without blanking the whole page. A 403 on a lookup means the
  // user can read fleet data but lacks access to the supporting directory.
  const [lookupDenied, setLookupDenied] = useState({ employees: false, locations: false });

  const loadData = useCallback(async () => {
    // Promise.allSettled via `settle()` — secondary lookup failures (403) do not
    // unmount the page. Each result carries `{ data, ok, denied, status }`.
    const [vR, aR, eR, sR, empR, locR, wiR, wuR] = await settle([
      api.get('/fleet/vehicles'),
      api.get('/fleet/alerts?status=open'),
      api.get('/fleet/alerts/expirations'),
      api.get('/fleet/stats'),
      // Use the lightweight directory endpoints — auth-only, no module grant
      // needed. A fleet-only user can populate driver/location dropdowns even
      // without `employees:view` or `locations:view`.
      lookupsAPI.employees({ is_active: true }),
      lookupsAPI.locations({ is_active: true }),
      api.get('/fleet/wex/imports'),
      api.get('/fleet/wex/unmatched'),
    ]);
    if (vR.ok) setVehicles(vR.data); else if (!vR.denied) toast.error('Failed to load vehicles');
    if (aR.ok) setAlerts(aR.data);
    if (eR.ok) setExpirationAlerts(eR.data);
    if (sR.ok) setStats(sR.data);
    setEmployees(empR.ok ? empR.data : []);
    setLocations(locR.ok ? locR.data : []);
    setLookupDenied({ employees: !empR.ok && empR.denied, locations: !locR.ok && locR.denied });
    if (wiR.ok) setWexImports(wiR.data);
    if (wuR.ok) setWexUnmatched(wuR.data);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadVehicleDetails = async (vehicle) => {
    setSelectedVehicle(vehicle);
    try {
      const [mRes, fRes, cRes, dRes] = await Promise.all([
        api.get(`/fleet/vehicles/${vehicle.id}/mileage`),
        api.get(`/fleet/vehicles/${vehicle.id}/fuel`),
        api.get(`/fleet/charts/fuel?vehicle_id=${vehicle.id}`),
        api.get(`/fleet/vehicles/${vehicle.id}/documents`),
      ]);
      setVehicleMileage(mRes.data);
      setVehicleFuel(fRes.data);
      setFuelChartData(cRes.data);
      setVehicleDocs(dRes.data);
    } catch (error) { console.error(error); }
  };

  const loadCosts = useCallback(async (months) => {
    try {
      const r = await api.get(`/fleet/costs?months=${months}`);
      setCosts(r.data);
    } catch { toast.error('Failed to load cost data'); }
  }, []);

  const handleUploadDoc = async (file) => {
    if (!selectedVehicle) return;
    setUploadingDoc(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docForm.doc_type);
    if (docForm.expiration_date) fd.append('expiration_date', docForm.expiration_date);
    try {
      await api.post(`/fleet/vehicles/${selectedVehicle.id}/documents/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setDocForm({ doc_type: 'insurance', expiration_date: '' });
      const r = await api.get(`/fleet/vehicles/${selectedVehicle.id}/documents`);
      setVehicleDocs(r.data);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingDoc(false); }
  };

  const handleDownloadDoc = async (doc) => {
    try {
      const r = await api.get(`/fleet/documents/${doc.id}/download`, { responseType: 'blob' });
      const u = window.URL.createObjectURL(new Blob([r.data], { type: doc.content_type }));
      const a = document.createElement('a'); a.href = u; a.download = doc.filename; a.click();
    } catch { toast.error('Download failed'); }
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete ${doc.filename}?`)) return;
    try {
      await api.delete(`/fleet/documents/${doc.id}`);
      setVehicleDocs(vehicleDocs.filter(d => d.id !== doc.id));
      toast.success('Document deleted');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Delete failed'); }
  };

  // ===== VEHICLE CRUD =====
  const handleSaveVehicle = async () => {
    if (!vehicleForm.make || !vehicleForm.model) { toast.error('Make and model are required'); return; }
    setSaving(true);
    try {
      const payload = { ...vehicleForm };
      // Convert numeric fields
      if (payload.year) payload.year = parseInt(payload.year) || null;
      if (payload.current_balance) payload.current_balance = parseFloat(payload.current_balance) || null;
      if (payload.payment_amount) payload.payment_amount = parseFloat(payload.payment_amount) || null;
      if (payload.interest_rate) payload.interest_rate = parseFloat(payload.interest_rate) || null;
      if (payload.max_gallons_threshold) payload.max_gallons_threshold = parseFloat(payload.max_gallons_threshold) || 30;
      // Clear empty strings
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });

      if (editingVehicle) {
        await api.put(`/fleet/vehicles/${editingVehicle.id}`, payload);
        toast.success('Vehicle updated');
      } else {
        await api.post('/fleet/vehicles', payload);
        toast.success('Vehicle added');
      }
      setShowVehicle(false);
      setEditingVehicle(null);
      setVehicleForm({ ...emptyVehicle });
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save vehicle'); }
    finally { setSaving(false); }
  };

  const openEditVehicle = (v) => {
    setEditingVehicle(v);
    setVehicleForm({ ...emptyVehicle, ...v, year: v.year || '', current_balance: v.current_balance || '', payment_amount: v.payment_amount || '', interest_rate: v.interest_rate || '', max_gallons_threshold: v.max_gallons_threshold || 30 });
    setShowVehicle(true);
  };

  const handleDeleteVehicle = async (v) => {
    if (!window.confirm(`Delete ${v.year} ${v.make} ${v.model}?`)) return;
    try { await api.delete(`/fleet/vehicles/${v.id}`); toast.success('Vehicle deleted'); loadData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };

  // ===== FUEL =====
  const handleAddFuel = async () => {
    if (!fuelForm.vehicle_id || !fuelForm.date || !fuelForm.gallons) { toast.error('Vehicle, date, and gallons required'); return; }
    setSaving(true);
    try {
      const payload = { ...fuelForm, gallons: parseFloat(fuelForm.gallons), cost_per_gallon: parseFloat(fuelForm.cost_per_gallon) || 0, total_cost: parseFloat(fuelForm.total_cost) || 0, odometer: parseInt(fuelForm.odometer) || null };
      const res = await api.post('/fleet/fuel', payload);
      toast.success('Fuel entry recorded');
      setShowFuel(false);
      setFuelForm({ vehicle_id: '', date: '', gallons: '', cost_per_gallon: '', total_cost: '', odometer: '', location: '', notes: '' });
      loadData();
      if (selectedVehicle && selectedVehicle.id === fuelForm.vehicle_id) loadVehicleDetails(selectedVehicle);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add fuel entry'); }
    finally { setSaving(false); }
  };

  // ===== MILEAGE =====
  const handleAddMileage = async () => {
    if (!selectedVehicle || !mileageForm.total_mileage) { toast.error('Enter mileage'); return; }
    setSaving(true);
    try {
      await api.post(`/fleet/vehicles/${selectedVehicle.id}/mileage`, { month: parseInt(mileageForm.month), year: parseInt(mileageForm.year), total_mileage: parseInt(mileageForm.total_mileage) });
      toast.success('Mileage recorded');
      setShowMileage(false);
      loadVehicleDetails(selectedVehicle);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  // ===== ALERTS =====
  const handleResolveAlert = async (alertId) => {
    try { await api.post(`/fleet/alerts/${alertId}/resolve`); toast.success('Alert resolved'); loadData(); }
    catch { toast.error('Failed to resolve'); }
  };

  const handleFleetImport = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/fleet/import/vehicles', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data;
      const parts = [];
      if (d.created) parts.push(`${d.created} added`);
      if (d.updated) parts.push(`${d.updated} updated`);
      if (d.unchanged) parts.push(`${d.unchanged} unchanged`);
      if (d.skipped) parts.push(`${d.skipped} skipped`);
      toast.success(parts.length ? `Import complete — ${parts.join(', ')}` : 'Import complete — nothing to change');
      if (d.protected?.length > 0) toast.info(`${d.protected.length} field(s) kept their TriCorps value (edited here, not overwritten)`);
      if (d.needs_review?.length > 0) {
        toast.warning(`${d.needs_review.length} row(s) need review — nothing on those rows was applied`, { duration: 10000 });
        d.needs_review.slice(0, 3).forEach(r => toast.warning(`Row ${r.row}${r.vehicle ? ` (${r.vehicle})` : ''}: ${r.reason}`, { duration: 12000 }));
      }
      if (d.errors?.length > 0) d.errors.slice(0, 3).forEach(e => toast.warning(`Row ${e.row}: ${e.error}`));
      loadData();
      if (costs) loadCosts(costMonths);
    } catch (error) { toast.error(error.response?.data?.detail || 'Import failed'); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/fleet/import/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'fleet_import_template.csv'; a.click();
    } catch { toast.error('Download failed'); }
  };

  const loadMileageAudit = async (vehicleId) => {
    try {
      const res = await api.get(`/fleet/vehicles/${vehicleId}/mileage-audit`);
      setMileageAuditLog(res.data);
      setShowMileageAudit(true);
    } catch { toast.error('Failed to load audit log'); }
  };

  const loadBulkMileage = async (m, y) => {
    try {
      const res = await api.get(`/fleet/mileage/bulk?month=${m}&year=${y}`);
      setBulkMileage(res.data);
      const entries = {};
      res.data.vehicles.forEach(v => { if (v.total_mileage != null) entries[v.vehicle_id] = String(v.total_mileage); });
      setBulkEntries(entries);
    } catch { toast.error('Failed to load mileage data'); }
  };

  const handleBulkMileageSave = async () => {
    const entries = Object.entries(bulkEntries).filter(([_, val]) => val !== '' && val != null).map(([vid, val]) => ({ vehicle_id: vid, total_mileage: parseInt(val) }));
    if (entries.length === 0) { toast.error('No mileage entries to save'); return; }
    setSaving(true);
    try {
      const res = await api.post('/fleet/mileage/bulk', { month: bulkMonth, year: bulkYear, entries });
      toast.success(res.data.message);
      loadBulkMileage(bulkMonth, bulkYear);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  // ===== WEX IMPORT =====
  const handleWexImport = async (file, mode = 'new') => {
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/fleet/wex/import?mode=${mode}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.status === 'duplicate') {
        setPendingFile(file);
        setShowDuplicatePrompt(res.data);
      } else {
        toast.success(res.data.message);
        if (res.data.unmatched > 0) toast.warning(`${res.data.unmatched} unmatched transactions need review`);
        setPendingFile(null);
        setShowDuplicatePrompt(null);
        loadData();
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  const handleAssignUnmatched = async (txId, vehicleId) => {
    try {
      await api.post(`/fleet/wex/unmatched/${txId}/assign`, { vehicle_id: vehicleId });
      toast.success('Transaction assigned');
      setShowAssignDialog(null);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to assign'); }
  };

  const handleDismissUnmatched = async (txId) => {
    try {
      await api.post(`/fleet/wex/unmatched/${txId}/dismiss`);
      toast.success('Transaction dismissed');
      loadData();
    } catch { toast.error('Failed'); }
  };

  const filteredVehicles = vehicles.filter(v => {
    const term = search.toLowerCase();
    return `${v.make} ${v.model} ${v.year} ${v.vin || ''} ${v.tag_number || ''} ${v.assigned_employee_name || ''} ${v.assigned_location_name || ''}`.toLowerCase().includes(term);
  });

  const sortedVehicles = useMemo(() => sortRows(filteredVehicles, vehicleSort.sortKey, vehicleSort.sortDir, {
    vehicle: v => `${v.make || ''} ${v.model || ''}`.trim(),
    year: v => v.year,
    vin: v => v.vin,
    tag: v => v.tag_number,
    tag_expiration: v => v.tag_expiration,
    assigned: v => v.assigned_employee_name || v.assigned_location_name,
    gas_card: v => v.gas_card_number,
  }), [filteredVehicles, vehicleSort.sortKey, vehicleSort.sortDir]);

  // Mileage rows carry the value currently typed in the input so sorting by
  // "Current Mileage" or "% Change" reflects what the user sees.
  const mileageRows = useMemo(() => {
    if (!bulkMileage) return [];
    const rows = bulkMileage.vehicles.map(v => {
      const raw = bulkEntries[v.vehicle_id] ?? '';
      const current = raw === '' ? null : parseInt(raw, 10);
      const pct = (current != null && v.prev_mileage > 0) ? round(((current - v.prev_mileage) / v.prev_mileage) * 100, 1) : null;
      return { ...v, current_value: raw, current, pct };
    });
    return sortRows(rows, mileageSort.sortKey, mileageSort.sortDir, {
      current: r => r.current,
      pct: r => r.pct,
      prev_mileage: r => r.prev_mileage,
    });
  }, [bulkMileage, bulkEntries, mileageSort.sortKey, mileageSort.sortDir]);

  const sortedUnmatched = useMemo(() => sortRows(wexUnmatched, wexSort.sortKey, wexSort.sortDir), [wexUnmatched, wexSort.sortKey, wexSort.sortDir]);

  const sortedCostRows = useMemo(() => sortRows(costs?.vehicles || [], costSort.sortKey, costSort.sortDir), [costs, costSort.sortKey, costSort.sortDir]);

  return (
    <div className="space-y-6" data-testid="fleet-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Fleet Management</h1>
          <p className="text-slate-500 mt-1">Track vehicles, mileage, fuel, and maintenance</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          {canExport && (
            <Button variant="outline" onClick={handleDownloadTemplate} data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-2" /> Template
            </Button>
          )}
          {canAdd && (
            <label className="cursor-pointer">
              <input type="file" accept=".csv" className="hidden" onChange={(e) => { if (e.target.files[0]) handleFleetImport(e.target.files[0]); e.target.value = ''; }} />
              <Button variant="outline" asChild data-testid="import-fleet-btn"><span><Upload className="w-4 h-4 mr-2" />Import CSV</span></Button>
            </label>
          )}
          {canAdd && (
            <Button variant="outline" onClick={() => setShowFuel(true)} data-testid="add-fuel-btn">
              <Fuel className="w-4 h-4 mr-2" /> Log Fuel
            </Button>
          )}
          {canAdd && (
            <Button onClick={() => { setEditingVehicle(null); setVehicleForm({ ...emptyVehicle }); setShowVehicle(true); }} data-testid="add-vehicle-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Vehicle
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Car className="w-5 h-5 text-blue-600" /></div><div><p className="text-2xl font-bold">{stats.total_vehicles || 0}</p><p className="text-sm text-slate-500">Vehicles</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className={`w-10 h-10 rounded-lg flex items-center justify-center ${alerts.length > 0 ? 'bg-red-100' : 'bg-slate-100'}`}><AlertTriangle className={`w-5 h-5 ${alerts.length > 0 ? 'text-red-600' : 'text-slate-400'}`} /></div><div><p className={`text-2xl font-bold ${alerts.length > 0 ? 'text-red-600' : ''}`}>{alerts.length}</p><p className="text-sm text-slate-500">Open Alerts</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><Fuel className="w-5 h-5 text-emerald-600" /></div><div><p className="text-2xl font-bold">{stats.total_fuel_entries || 0}</p><p className="text-sm text-slate-500">Fuel Entries</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><DollarSign className="w-5 h-5 text-purple-600" /></div><div><p className="text-2xl font-bold">${(stats.month_fuel_cost || 0).toLocaleString()}</p><p className="text-sm text-slate-500">Fuel This Month</p></div></CardContent></Card>
      </div>

      {/* Expiration Alerts */}
      {expirationAlerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="expiration-alerts">
          <div className="flex items-center gap-2 mb-2"><Calendar className="w-4 h-4 text-amber-600" /><span className="font-medium text-amber-800">Upcoming Expirations & Due Dates</span></div>
          <div className="space-y-1">
            {expirationAlerts.slice(0, 5).map((a, i) => (
              <div key={i} className="text-sm"><Badge variant={a.overdue ? 'destructive' : 'outline'} className="text-xs mr-2">{a.overdue ? 'OVERDUE' : a.type.replace('_', ' ')}</Badge>{a.vehicle_name} — {a.date}{a.amount ? ` ($${a.amount})` : ''}</div>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="vehicles" className="space-y-4">
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="w-max">
            <TabsTrigger value="vehicles">Vehicles ({vehicles.length})</TabsTrigger>
            <TabsTrigger value="mileage" onClick={() => { if (!bulkMileage) loadBulkMileage(bulkMonth, bulkYear); }}>Monthly Mileage</TabsTrigger>
            <TabsTrigger value="wex">WEX Import {wexUnmatched.length > 0 && <Badge variant="destructive" className="ml-1 text-xs">{wexUnmatched.length}</Badge>}</TabsTrigger>
            <TabsTrigger value="alerts">Fraud Alerts ({alerts.length})</TabsTrigger>
            <TabsTrigger value="costs" onClick={() => { if (!costs) loadCosts(costMonths); }} data-testid="costs-tab">Cost per Mile</TabsTrigger>
            <TabsTrigger value="charts">Charts & Reports</TabsTrigger>
          </TabsList>
        </div>

        {/* VEHICLES TAB */}
        <TabsContent value="vehicles">
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex gap-3">
                <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><Input placeholder="Search vehicles..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="fleet-search" /></div>
                <Button variant="outline" onClick={async () => { try { const r = await api.get('/fleet/export/vehicles', { responseType: 'blob' }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = 'fleet_vehicles.csv'; a.click(); } catch { toast.error('Export failed'); } }} data-testid="export-vehicles-btn"><Download className="w-4 h-4 mr-2" />Export</Button>
              </div>
              {loading ? <div className="p-8"><div className="h-12 skeleton-pulse rounded" /></div> : (
                <AccessState
                  module="fleet"
                  action="view"
                  items={sortedVehicles}
                  loading={loading}
                  entityLabel={search ? 'vehicles matching your search' : 'vehicles'}
                  emptyAction={canAdd ? <Button onClick={() => { setEditingVehicle(null); setVehicleForm({ ...emptyVehicle }); setShowVehicle(true); }} data-testid="empty-add-vehicle-btn"><Plus className="w-4 h-4 mr-2" />Add Vehicle</Button> : null}
                >
                  <Table>
                    <TableHeader><TableRow>
                      <FleetSortHeader label="Vehicle" colKey="vehicle" {...vehicleSort} />
                      <FleetSortHeader label="Year" colKey="year" {...vehicleSort} />
                      <FleetSortHeader label="VIN" colKey="vin" {...vehicleSort} />
                      <FleetSortHeader label="Tag" colKey="tag" {...vehicleSort} />
                      <FleetSortHeader label="Tag Exp" colKey="tag_expiration" {...vehicleSort} />
                      <FleetSortHeader label="Assigned To" colKey="assigned" {...vehicleSort} />
                      <FleetSortHeader label="Gas Card" colKey="gas_card" {...vehicleSort} />
                      <TableHead>Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {sortedVehicles.map(v => (
                        <TableRow key={v.id} className="cursor-pointer" onClick={() => loadVehicleDetails(v)} data-testid={`vehicle-row-${v.id}`}>
                          <TableCell><p className="font-medium">{v.make} {v.model}</p><p className="text-xs text-slate-500">{v.color}</p></TableCell>
                          <TableCell>{v.year || '-'}</TableCell>
                          <TableCell className="font-mono text-xs">{v.vin || '-'}</TableCell>
                          <TableCell>{v.tag_number || '-'}<br/><span className="text-xs text-slate-500">{v.tag_state}</span></TableCell>
                          <TableCell className="text-sm">{v.tag_expiration || '-'}</TableCell>
                          <TableCell>{v.assigned_employee_name || v.assigned_location_name || <span className="text-slate-400">Unassigned</span>}</TableCell>
                          <TableCell className="font-mono text-sm">{v.gas_card_number || '-'}</TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className="flex gap-1">
                              {canEdit && <Button variant="ghost" size="sm" onClick={() => openEditVehicle(v)}><Edit className="w-4 h-4" /></Button>}
                              {canDelete && <Button variant="ghost" size="sm" onClick={() => handleDeleteVehicle(v)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                              {!canEdit && !canDelete && <span className="text-xs text-slate-400 italic">View only</span>}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccessState>
              )}
            </CardContent>
          </Card>

          {/* Selected Vehicle Detail — opens as a dialog so you never hunt for it at the bottom */}
          <Dialog open={!!selectedVehicle} onOpenChange={(o) => { if (!o) setSelectedVehicle(null); }}>
            <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-5xl max-h-[88vh] overflow-y-auto overflow-x-hidden" data-testid="vehicle-detail-dialog">
              {selectedVehicle && (
                <>
                  <DialogHeader className="pr-8">
                    <DialogTitle>{selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}</DialogTitle>
                    <DialogDescription className="break-words">VIN: {selectedVehicle.vin || '-'} | Gas Card: {selectedVehicle.gas_card_number || '-'}</DialogDescription>
                    <div className="flex flex-wrap gap-2 pt-2">
                      {canEdit && <Button size="sm" variant="outline" onClick={() => { setMileageForm({ month: new Date().getMonth() + 1, year: new Date().getFullYear(), total_mileage: '' }); setShowMileage(true); }} data-testid="add-mileage-btn"><Gauge className="w-4 h-4 mr-1" />Log Mileage</Button>}
                      <Button size="sm" variant="outline" onClick={() => loadMileageAudit(selectedVehicle.id)} data-testid="mileage-audit-btn"><History className="w-4 h-4 mr-1" />Mileage History</Button>
                      {canEdit && <Button size="sm" variant="outline" onClick={() => openEditVehicle(selectedVehicle)} data-testid="edit-vehicle-from-detail-btn"><Edit className="w-4 h-4 mr-1" />Edit Vehicle</Button>}
                    </div>
                  </DialogHeader>
                  <div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Mileage */}
                  <div>
                    <h4 className="font-medium mb-2">Monthly Mileage</h4>
                    {vehicleMileage.length > 0 ? (
                      <Table><TableHeader><TableRow><TableHead>Period</TableHead><TableHead className="text-right">Miles</TableHead><TableHead className="text-right">% Change</TableHead></TableRow></TableHeader>
                        <TableBody>{vehicleMileage.slice(0, 12).map((m, i) => (
                          <TableRow key={i}><TableCell>{m.month}/{m.year}</TableCell><TableCell className="text-right font-medium">{m.total_mileage?.toLocaleString()}</TableCell><TableCell className="text-right">{m.pct_change != null ? <span className={m.pct_change > 0 ? 'text-red-600' : 'text-emerald-600'}>{m.pct_change > 0 ? '+' : ''}{m.pct_change}%</span> : '-'}</TableCell></TableRow>
                        ))}</TableBody>
                      </Table>
                    ) : <p className="text-sm text-slate-400">No mileage logged yet</p>}
                  </div>
                  {/* Fuel */}
                  <div>
                    <h4 className="font-medium mb-2">Recent Fuel</h4>
                    {vehicleFuel.length > 0 ? (
                      <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead className="text-right">Gal</TableHead><TableHead className="text-right">$/Gal</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
                        <TableBody>{vehicleFuel.slice(0, 10).map((f, i) => (
                          <TableRow key={i}><TableCell>{f.date}</TableCell><TableCell className="text-right">{f.gallons}</TableCell><TableCell className="text-right">${f.cost_per_gallon?.toFixed(2)}</TableCell><TableCell className="text-right font-medium">${f.total_cost?.toFixed(2)}</TableCell></TableRow>
                        ))}</TableBody>
                      </Table>
                    ) : <p className="text-sm text-slate-400">No fuel entries yet</p>}
                  </div>
                </div>
                {/* Documents */}
                <div className="mt-6" data-testid="vehicle-documents">
                  <div className="flex items-end justify-between flex-wrap gap-3 mb-2">
                    <h4 className="font-medium flex items-center gap-2"><Paperclip className="w-4 h-4" /> Documents</h4>
                    {canEdit && (
                      <div className="flex flex-wrap items-end gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Type</Label>
                          <Select value={docForm.doc_type} onValueChange={(v) => setDocForm({ ...docForm, doc_type: v })}>
                            <SelectTrigger className="w-[150px] h-9" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Expires (optional)</Label>
                          <Input type="date" className="w-[150px] h-9" value={docForm.expiration_date} onChange={(e) => setDocForm({ ...docForm, expiration_date: e.target.value })} data-testid="doc-expiration-input" />
                        </div>
                        <label className="cursor-pointer">
                          <input type="file" className="hidden" onChange={(e) => { if (e.target.files[0]) handleUploadDoc(e.target.files[0]); e.target.value = ''; }} />
                          <Button asChild size="sm" disabled={uploadingDoc} data-testid="upload-doc-btn"><span><Upload className="w-4 h-4 mr-1" />{uploadingDoc ? 'Uploading...' : 'Upload'}</span></Button>
                        </label>
                      </div>
                    )}
                  </div>
                  {vehicleDocs.length > 0 ? (
                    <Table>
                      <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>File</TableHead><TableHead>Expires</TableHead><TableHead>Uploaded</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {vehicleDocs.map(d => {
                          const expired = d.expiration_date && d.expiration_date < new Date().toISOString().slice(0, 10);
                          return (
                            <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                              <TableCell><Badge variant="outline" className="text-xs capitalize">{d.doc_type}</Badge></TableCell>
                              <TableCell className="text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400" />{d.filename}</TableCell>
                              <TableCell className="text-sm">{d.expiration_date ? (expired ? <Badge variant="destructive" className="text-xs">Expired {d.expiration_date}</Badge> : d.expiration_date) : '-'}</TableCell>
                              <TableCell className="text-xs text-slate-500">{formatDate(d.uploaded_at)}<br />{d.uploaded_by_name}</TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" onClick={() => handleDownloadDoc(d)} data-testid={`download-doc-${d.id}`}><Download className="w-4 h-4" /></Button>
                                  {canDelete && <Button size="sm" variant="ghost" onClick={() => handleDeleteDoc(d)} data-testid={`delete-doc-${d.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ) : <p className="text-sm text-slate-400">No insurance, registration or title documents on file yet</p>}
                </div>

                {/* MPG Chart */}
                {fuelChartData.length > 1 && (
                  <div className="mt-6">
                    <h4 className="font-medium mb-2">Fuel & MPG Trend</h4>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={fuelChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                        <XAxis dataKey="month" stroke={chart.axis} tick={{ fill: chart.axis, fontSize: 12 }} />
                        <YAxis yAxisId="left" stroke={chart.axis} tick={{ fill: chart.axis, fontSize: 12 }} />
                        <YAxis yAxisId="right" orientation="right" stroke={chart.axis} tick={{ fill: chart.axis, fontSize: 12 }} />
                        <Tooltip contentStyle={{ backgroundColor: chart.surface, border: `1px solid ${chart.border}`, borderRadius: 6, color: chart.text }} labelStyle={{ color: chart.text }} itemStyle={{ color: chart.text }} />
                        <Legend wrapperStyle={{ color: chart.axis }} />
                        <Bar yAxisId="left" dataKey="gallons" fill={chart.series[0]} name="Gallons" />
                        <Line yAxisId="right" type="monotone" dataKey="mpg" stroke={chart.series[1]} name="MPG" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* MONTHLY MILEAGE TAB */}
        <TabsContent value="mileage">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div><CardTitle>Monthly Mileage Update</CardTitle><CardDescription>Enter mileage for all vehicles in a single session</CardDescription></div>
                <div className="flex items-center gap-3">
                  <Select value={String(bulkMonth)} onValueChange={(v) => { const m = parseInt(v); setBulkMonth(m); loadBulkMileage(m, bulkYear); }}>
                    <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <SelectItem key={m} value={String(m)}>{new Date(2000, m-1).toLocaleString('default', {month:'long'})}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" className="w-[90px]" value={bulkYear} onChange={(e) => { const y = parseInt(e.target.value); setBulkYear(y); loadBulkMileage(bulkMonth, y); }} />
                  {canEdit && (
                    <Button onClick={handleBulkMileageSave} disabled={saving} data-testid="save-bulk-mileage-btn">
                      <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save All'}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {bulkMileage ? (
                <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <FleetSortHeader label="Vehicle" colKey="vehicle_name" {...mileageSort} />
                      <FleetSortHeader label="Assigned To" colKey="assigned_to" {...mileageSort} />
                      <FleetSortHeader label="Gas Card" colKey="gas_card_number" {...mileageSort} />
                      <FleetSortHeader label="Prev Month" colKey="prev_mileage" {...mileageSort} className="text-right" />
                      <FleetSortHeader label="Current Mileage" colKey="current" {...mileageSort} className="w-[140px]" />
                      <FleetSortHeader label="% Change" colKey="pct" {...mileageSort} className="text-right" />
                      <FleetSortHeader label="Source" colKey="source" {...mileageSort} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mileageRows.map(v => {
                      const currentVal = v.current_value;
                      const prevVal = v.prev_mileage;
                      const pctChange = v.pct;
                      return (
                        <TableRow key={v.vehicle_id}>
                          <TableCell className="font-medium">{v.vehicle_name}</TableCell>
                          <TableCell className="text-sm">{v.assigned_to || <span className="text-slate-400">-</span>}</TableCell>
                          <TableCell className="font-mono text-sm">{v.gas_card_number || '-'}</TableCell>
                          <TableCell className="text-right text-sm">{prevVal != null ? prevVal.toLocaleString() : '-'}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              className="h-8"
                              value={currentVal}
                              onChange={(e) => setBulkEntries({ ...bulkEntries, [v.vehicle_id]: e.target.value })}
                              placeholder="Enter miles"
                              data-testid={`bulk-mile-${v.vehicle_id}`}
                            />
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {pctChange != null ? (
                              <span className={pctChange > 0 ? 'text-red-600' : 'text-emerald-600'}>{pctChange > 0 ? '+' : ''}{pctChange}%</span>
                            ) : '-'}
                          </TableCell>
                          <TableCell>
                            {v.source === 'import' && <Badge className="bg-blue-100 text-blue-700 text-xs">WEX Import</Badge>}
                            {v.source === 'edited' && <Badge className="bg-amber-100 text-amber-700 text-xs">Edited</Badge>}
                            {v.source === 'manual' && v.total_mileage != null && <Badge variant="outline" className="text-xs">Manual</Badge>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {canEdit && (
                  <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
                    <p className="text-sm text-slate-500" data-testid="bulk-mileage-filled-count">
                      {mileageRows.filter(r => r.current_value !== '' && r.current_value != null).length} of {mileageRows.length} vehicles filled in
                    </p>
                    <Button onClick={handleBulkMileageSave} disabled={saving} data-testid="save-bulk-mileage-sticky-btn">
                      <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save All'}
                    </Button>
                  </div>
                )}
                </>
              ) : (
                <div className="p-8 text-center text-slate-400">Loading mileage data...</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* WEX IMPORT TAB */}
        <TabsContent value="wex">
          <div className="space-y-4">
            {/* Upload Section */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div><CardTitle>WEX Fuel Transaction Import</CardTitle><CardDescription>Upload monthly WEX Excel export to automatically update vehicle fuel data</CardDescription></div>
                  {canEdit ? (
                    <label className="cursor-pointer">
                      <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { if (e.target.files[0]) handleWexImport(e.target.files[0]); e.target.value = ''; }} />
                      <Button asChild disabled={importing} data-testid="wex-upload-btn"><span><FileUp className="w-4 h-4 mr-2" />{importing ? 'Processing...' : 'Upload WEX File'}</span></Button>
                    </label>
                  ) : (
                    <span className="text-xs text-slate-400 italic" data-testid="wex-upload-denied">View only — fleet:edit required to upload</span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600 space-y-1">
                  <p>Accepted format: Excel (.xlsx) exported from WEX</p>
                  <p>Transactions are matched to vehicles by the <strong>Card Number</strong> field (masked format, e.g. ****00991)</p>
                  <p>The import month is detected automatically from transaction dates in the file</p>
                </div>
              </CardContent>
            </Card>

            {/* Unmatched Transactions */}
            {wexUnmatched.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Unmatched Transactions ({wexUnmatched.length})</CardTitle>
                  <CardDescription>These transactions could not be matched to a vehicle by card number. Assign or dismiss each one.</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader><TableRow>
                      <FleetSortHeader label="Date" colKey="date" {...wexSort} />
                      <FleetSortHeader label="Card #" colKey="card_number" {...wexSort} />
                      <FleetSortHeader label="Vehicle / Asset ID" colKey="asset_id" {...wexSort} />
                      <FleetSortHeader label="Driver" colKey="driver_name" {...wexSort} />
                      <FleetSortHeader label="Department" colKey="department" {...wexSort} />
                      <FleetSortHeader label="Gallons" colKey="gallons" {...wexSort} className="text-right" />
                      <FleetSortHeader label="Cost" colKey="total_cost" {...wexSort} className="text-right" />
                      <TableHead>Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {sortedUnmatched.map(tx => (
                        <TableRow key={tx.id}>
                          <TableCell>{tx.date}</TableCell>
                          <TableCell className="font-mono">{tx.card_number}</TableCell>
                          <TableCell><p className="text-sm">{tx.asset_id}</p><p className="text-xs text-slate-500">{tx.vehicle_description}</p></TableCell>
                          <TableCell>{tx.driver_name}</TableCell>
                          <TableCell>{tx.department}</TableCell>
                          <TableCell className="text-right">{tx.gallons?.toFixed(2)}</TableCell>
                          <TableCell className="text-right">${tx.total_cost?.toFixed(2)}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              {canEdit && <Button size="sm" variant="outline" onClick={() => setShowAssignDialog(tx)} data-testid={`assign-tx-${tx.id}`}><Link className="w-3 h-3 mr-1" />Assign</Button>}
                              {canEdit && <Button size="sm" variant="ghost" onClick={() => handleDismissUnmatched(tx.id)}><XIcon className="w-3 h-3 text-slate-400" /></Button>}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {/* Import History */}
            <Card>
              <CardHeader><CardTitle>Import History</CardTitle></CardHeader>
              <CardContent className="p-0">
                {wexImports.length > 0 ? (
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Period</TableHead><TableHead>File</TableHead><TableHead className="text-right">Transactions</TableHead><TableHead className="text-right">Matched</TableHead><TableHead className="text-right">Unmatched</TableHead><TableHead>Imported By</TableHead><TableHead>Date</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {wexImports.map(imp => (
                        <TableRow key={imp.id}>
                          <TableCell className="font-medium">{imp.month}/{imp.year}</TableCell>
                          <TableCell className="text-sm">{imp.filename}</TableCell>
                          <TableCell className="text-right">{imp.transaction_count}</TableCell>
                          <TableCell className="text-right text-emerald-600">{imp.matched_count}</TableCell>
                          <TableCell className="text-right">{imp.unmatched_count > 0 ? <span className="text-amber-600">{imp.unmatched_count}</span> : '0'}</TableCell>
                          <TableCell>{imp.imported_by_name}</TableCell>
                          <TableCell className="text-sm">{formatDate(imp.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-slate-400"><FileUp className="w-8 h-8 mx-auto mb-2 text-slate-300" /><p>No imports yet</p></div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ALERTS TAB */}
        <TabsContent value="alerts">
          <Card>
            <CardHeader><CardTitle>Fraud & Misuse Alerts</CardTitle><CardDescription>Flagged fuel transactions requiring review</CardDescription></CardHeader>
            <CardContent className="p-0">
              {alerts.length > 0 ? (
                <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Vehicle</TableHead><TableHead>Gas Card</TableHead><TableHead>Type</TableHead><TableHead>Reason</TableHead><TableHead>Details</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
                  <TableBody>{alerts.map(a => (
                    <TableRow key={a.id} data-testid={`alert-row-${a.id}`}>
                      <TableCell>{a.date}</TableCell><TableCell className="font-medium">{a.vehicle_name}</TableCell><TableCell className="font-mono text-sm">{a.gas_card_number || '-'}</TableCell>
                      <TableCell><Badge variant="destructive" className="text-xs">{a.type?.replace('_', ' ')}</Badge></TableCell>
                      <TableCell className="text-sm max-w-[250px]">{a.reason}</TableCell>
                      <TableCell className="text-xs">{a.details?.gallons && `${a.details.gallons} gal`}{a.details?.cost_per_gallon && ` @ $${a.details.cost_per_gallon}/gal`}</TableCell>
                      <TableCell>{canEdit && <Button size="sm" variant="outline" onClick={() => handleResolveAlert(a.id)} data-testid={`resolve-alert-${a.id}`}><CheckCircle className="w-3 h-3 mr-1" />Resolve</Button>}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              ) : <div className="text-center py-12 text-slate-500"><CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-300" /><p>No open alerts</p></div>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COST PER MILE TAB */}
        <TabsContent value="costs">
          <div className="space-y-4" data-testid="costs-panel">
            <Card>
              <CardHeader>
                <div className="flex items-end justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle>Cost per Mile & Total Cost of Ownership</CardTitle>
                    <CardDescription>Fuel spend plus note payments, divided by logged miles. Highest cost per mile first — those are the vehicles worth replacing.</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={String(costMonths)} onValueChange={(v) => { const m = parseInt(v); setCostMonths(m); loadCosts(m); }}>
                      <SelectTrigger className="w-[150px]" data-testid="cost-window-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">Last 3 months</SelectItem>
                        <SelectItem value="6">Last 6 months</SelectItem>
                        <SelectItem value="12">Last 12 months</SelectItem>
                        <SelectItem value="24">Last 24 months</SelectItem>
                      </SelectContent>
                    </Select>
                    {canExport && (
                      <Button variant="outline" onClick={async () => { try { const r = await api.get(`/fleet/export/costs?months=${costMonths}`, { responseType: 'blob' }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = 'fleet_cost_per_mile.csv'; a.click(); } catch { toast.error('Export failed'); } }} data-testid="export-costs-btn"><Download className="w-4 h-4 mr-2" />Export</Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {!costs ? <div className="p-8 text-center text-slate-400">Loading cost data...</div> : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 px-4 pb-4">
                      <div className="rounded-lg border p-3">
                        <p className="text-xs text-slate-500">Fleet Cost / Mile</p>
                        <p className="text-2xl font-bold" data-testid="fleet-cost-per-mile">{costs.totals.cost_per_mile != null ? `$${costs.totals.cost_per_mile.toFixed(3)}` : '—'}</p>
                        <p className="text-xs text-slate-400">{costs.totals.vehicles_in_average} vehicle(s) with a complete mileage log</p>
                      </div>
                      <div className="rounded-lg border p-3"><p className="text-xs text-slate-500">Total Cost ({costs.months} mo)</p><p className="text-2xl font-bold">${(costs.totals.total_cost || 0).toLocaleString()}</p></div>
                      <div className="rounded-lg border p-3"><p className="text-xs text-slate-500">Miles Logged</p><p className="text-2xl font-bold">{(costs.totals.miles || 0).toLocaleString()}</p></div>
                      <div className="rounded-lg border p-3">
                        <p className="text-xs text-slate-500">Excluded — Mileage Gaps</p>
                        <p className="text-2xl font-bold" data-testid="cost-excluded-count">{costs.totals.vehicles_excluded_incomplete_mileage || 0}</p>
                        <p className="text-xs text-slate-400">Left out of the average so gaps don't distort it</p>
                      </div>
                    </div>
                    {costs.totals.vehicles_excluded_incomplete_mileage > 0 && (
                      <div className="mx-4 mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="cost-mileage-gap-notice">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>
                          {costs.totals.vehicles_excluded_incomplete_mileage} vehicle(s) spent money in a month with no mileage entry. Their cost per mile is shown but marked <strong>mileage gaps</strong>, and they are kept out of the fleet average and the over-average flag. Fill in the missing months on the Monthly Mileage tab for a true figure.
                        </span>
                      </div>
                    )}
                    <Table>
                      <TableHeader><TableRow>
                        <FleetSortHeader label="Vehicle" colKey="vehicle_name" {...costSort} />
                        <FleetSortHeader label="Assigned To" colKey="assigned_to" {...costSort} />
                        <FleetSortHeader label="Miles" colKey="miles" {...costSort} className="text-right" />
                        <FleetSortHeader label="MPG" colKey="mpg" {...costSort} className="text-right" />
                        <FleetSortHeader label="Fuel" colKey="fuel_cost" {...costSort} className="text-right" />
                        <FleetSortHeader label="Payments" colKey="payment_cost" {...costSort} className="text-right" />
                        <FleetSortHeader label="Total" colKey="total_cost" {...costSort} className="text-right" />
                        <FleetSortHeader label="Cost / Mile" colKey="cost_per_mile" {...costSort} className="text-right" />
                      </TableRow></TableHeader>
                      <TableBody>
                        {sortedCostRows.map(r => {
                          const fleetCpm = costs.totals.cost_per_mile;
                          const hot = r.mileage_complete && r.cost_per_mile != null && fleetCpm != null && r.cost_per_mile > fleetCpm * 1.25;
                          return (
                            <TableRow key={r.vehicle_id} data-testid={`cost-row-${r.vehicle_id}`}>
                              <TableCell className="font-medium">{r.vehicle_name}</TableCell>
                              <TableCell className="text-sm">{r.assigned_to || <span className="text-slate-400">-</span>}</TableCell>
                              <TableCell className="text-right">{r.miles ? r.miles.toLocaleString() : '-'}</TableCell>
                              <TableCell className="text-right">{r.mpg ?? '-'}</TableCell>
                              <TableCell className="text-right">${(r.fuel_cost || 0).toLocaleString()}</TableCell>
                              <TableCell className="text-right">${(r.payment_cost || 0).toLocaleString()}</TableCell>
                              <TableCell className="text-right font-medium">${(r.total_cost || 0).toLocaleString()}</TableCell>
                              <TableCell className="text-right">
                                {r.cost_per_mile != null
                                  ? <span className={hot ? 'text-red-600 font-semibold' : ''}>${r.cost_per_mile.toFixed(3)}</span>
                                  : <span className="text-slate-400" title="No miles logged for this window">no miles</span>}
                                {!r.mileage_complete && r.missing_mileage_months?.length > 0 && (
                                  <Badge variant="outline" className="ml-2 text-xs border-amber-400 text-amber-700" data-testid={`cost-gap-${r.vehicle_id}`}
                                    title={`No mileage entry for: ${r.missing_mileage_months.join(', ')}`}>
                                    mileage gaps ({r.missing_mileage_months.length})
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* CHARTS TAB */}
        <TabsContent value="charts">
          <div className="space-y-4">
            <div className="flex gap-3">
              <Button variant="outline" onClick={async () => { try { const r = await api.get('/fleet/export/fuel', { responseType: 'blob' }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = 'fleet_fuel.csv'; a.click(); } catch { toast.error('Export failed'); } }} data-testid="export-fuel-btn"><Download className="w-4 h-4 mr-2" />Export Fuel Log</Button>
              <Button variant="outline" onClick={async () => { try { const r = await api.get('/fleet/export/mileage', { responseType: 'blob' }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.download = 'fleet_mileage.csv'; a.click(); } catch { toast.error('Export failed'); } }} data-testid="export-mileage-btn"><Download className="w-4 h-4 mr-2" />Export Mileage</Button>
            </div>
            <Card><CardHeader><CardTitle>Fleet-Wide Fuel Consumption</CardTitle></CardHeader><CardContent>
              <FleetWideChart />
            </CardContent></Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* ADD/EDIT VEHICLE DIALOG */}
      <Dialog open={showVehicle} onOpenChange={(o) => { setShowVehicle(o); if (!o) setEditingVehicle(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="vehicle-dialog">
          <DialogHeader><DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1"><Label>Make *</Label><Input value={vehicleForm.make} onChange={(e) => setVehicleForm({ ...vehicleForm, make: e.target.value })} data-testid="v-make" /></div>
              <div className="space-y-1"><Label>Model *</Label><Input value={vehicleForm.model} onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })} data-testid="v-model" /></div>
              <div className="space-y-1"><Label>Year</Label><Input type="number" value={vehicleForm.year} onChange={(e) => setVehicleForm({ ...vehicleForm, year: e.target.value })} data-testid="v-year" /></div>
              <div className="space-y-1"><Label>Color</Label><Input value={vehicleForm.color} onChange={(e) => setVehicleForm({ ...vehicleForm, color: e.target.value })} data-testid="v-color" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>VIN</Label><Input value={vehicleForm.vin} onChange={(e) => setVehicleForm({ ...vehicleForm, vin: e.target.value })} data-testid="v-vin" /></div>
              <div className="space-y-1"><Label>Tag #</Label><Input value={vehicleForm.tag_number} onChange={(e) => setVehicleForm({ ...vehicleForm, tag_number: e.target.value })} data-testid="v-tag" /></div>
              <div className="grid grid-cols-2 gap-2"><div className="space-y-1"><Label>Tag Exp (MM/YYYY)</Label><Input type="month" value={vehicleForm.tag_expiration} onChange={(e) => setVehicleForm({ ...vehicleForm, tag_expiration: e.target.value })} /></div><div className="space-y-1"><Label>State</Label><Input value={vehicleForm.tag_state} onChange={(e) => setVehicleForm({ ...vehicleForm, tag_state: e.target.value })} /></div></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Assign to Employee</Label><Select value={vehicleForm.assigned_to_employee_id || 'none'} onValueChange={(v) => setVehicleForm({ ...vehicleForm, assigned_to_employee_id: v === 'none' ? '' : v })} disabled={lookupDenied.employees}><SelectTrigger data-testid="vehicle-assign-employee-select"><SelectValue placeholder={lookupDenied.employees ? 'No access to employee directory' : 'None'} /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent></Select>{lookupDenied.employees && <p className="text-xs text-muted-foreground" data-testid="employee-lookup-denied-note">You don&apos;t have access to assign a driver. Other fleet actions still work.</p>}</div>
              <div className="space-y-1"><Label>Assign to Location</Label><Select value={vehicleForm.assigned_to_location_id || 'none'} onValueChange={(v) => setVehicleForm({ ...vehicleForm, assigned_to_location_id: v === 'none' ? '' : v })} disabled={lookupDenied.locations}><SelectTrigger data-testid="vehicle-assign-location-select"><SelectValue placeholder={lookupDenied.locations ? 'No access to locations directory' : 'None'} /></SelectTrigger><SelectContent><SelectItem value="none">None</SelectItem>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>{lookupDenied.locations && <p className="text-xs text-muted-foreground" data-testid="location-lookup-denied-note">You don&apos;t have access to assign a location. Other fleet actions still work.</p>}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Purpose</Label><Input value={vehicleForm.purpose} onChange={(e) => setVehicleForm({ ...vehicleForm, purpose: e.target.value })} placeholder="e.g., Patrol, Maintenance" /></div>
              <div className="space-y-1"><Label>Manager / Contact</Label><Input value={vehicleForm.manager_contact} onChange={(e) => setVehicleForm({ ...vehicleForm, manager_contact: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Gas Card #</Label><Input value={vehicleForm.gas_card_number} onChange={(e) => setVehicleForm({ ...vehicleForm, gas_card_number: e.target.value })} data-testid="v-gascard" /></div>
              <div className="space-y-1"><Label>Max Gallons Threshold</Label><Input type="number" value={vehicleForm.max_gallons_threshold} onChange={(e) => setVehicleForm({ ...vehicleForm, max_gallons_threshold: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Warranty Details</Label><Input value={vehicleForm.warranty_details} onChange={(e) => setVehicleForm({ ...vehicleForm, warranty_details: e.target.value })} /></div>
              <div className="space-y-1"><Label>Warranty Expiration</Label><Input type="date" value={vehicleForm.warranty_expiration} onChange={(e) => setVehicleForm({ ...vehicleForm, warranty_expiration: e.target.value })} /></div>
            </div>
            <p className="text-sm font-medium text-slate-600 pt-2 border-t">Financial Details</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Date Opened</Label><Input type="date" value={vehicleForm.date_opened} onChange={(e) => setVehicleForm({ ...vehicleForm, date_opened: e.target.value })} /></div>
              <div className="space-y-1"><Label>Maturity Date</Label><Input type="date" value={vehicleForm.maturity_date} onChange={(e) => setVehicleForm({ ...vehicleForm, maturity_date: e.target.value })} /></div>
              <div className="space-y-1"><Label>Payment Due</Label><Input type="date" value={vehicleForm.payment_due_date} onChange={(e) => setVehicleForm({ ...vehicleForm, payment_due_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Balance ($)</Label><Input type="number" step="0.01" value={vehicleForm.current_balance} onChange={(e) => setVehicleForm({ ...vehicleForm, current_balance: e.target.value })} /></div>
              <div className="space-y-1"><Label>Payment ($)</Label><Input type="number" step="0.01" value={vehicleForm.payment_amount} onChange={(e) => setVehicleForm({ ...vehicleForm, payment_amount: e.target.value })} /></div>
              <div className="space-y-1"><Label>Interest Rate (%)</Label><Input type="number" step="0.01" value={vehicleForm.interest_rate} onChange={(e) => setVehicleForm({ ...vehicleForm, interest_rate: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Notes</Label><Textarea value={vehicleForm.notes} onChange={(e) => setVehicleForm({ ...vehicleForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowVehicle(false)}>Cancel</Button><Button onClick={handleSaveVehicle} disabled={saving} data-testid="save-vehicle-btn">{saving ? 'Saving...' : editingVehicle ? 'Update' : 'Add Vehicle'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FUEL ENTRY DIALOG */}
      <Dialog open={showFuel} onOpenChange={setShowFuel}>
        <DialogContent data-testid="fuel-dialog">
          <DialogHeader><DialogTitle>Log Fuel Entry</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1"><Label>Vehicle *</Label><Select value={fuelForm.vehicle_id || 'none'} onValueChange={(v) => setFuelForm({ ...fuelForm, vehicle_id: v === 'none' ? '' : v })}><SelectTrigger data-testid="fuel-vehicle"><SelectValue placeholder="Select vehicle" /></SelectTrigger><SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Date *</Label><Input type="date" value={fuelForm.date} onChange={(e) => setFuelForm({ ...fuelForm, date: e.target.value })} data-testid="fuel-date" /></div>
              <div className="space-y-1"><Label>Gallons *</Label><Input type="number" step="0.01" value={fuelForm.gallons} onChange={(e) => setFuelForm({ ...fuelForm, gallons: e.target.value })} data-testid="fuel-gallons" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>$/Gallon</Label><Input type="number" step="0.01" value={fuelForm.cost_per_gallon} onChange={(e) => { const cpg = e.target.value; const tc = (parseFloat(cpg) || 0) * (parseFloat(fuelForm.gallons) || 0); setFuelForm({ ...fuelForm, cost_per_gallon: cpg, total_cost: tc.toFixed(2) }); }} data-testid="fuel-cpg" /></div>
              <div className="space-y-1"><Label>Total Cost</Label><Input type="number" step="0.01" value={fuelForm.total_cost} onChange={(e) => setFuelForm({ ...fuelForm, total_cost: e.target.value })} data-testid="fuel-total" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Odometer</Label><Input type="number" value={fuelForm.odometer} onChange={(e) => setFuelForm({ ...fuelForm, odometer: e.target.value })} /></div>
              <div className="space-y-1"><Label>Location</Label><Input value={fuelForm.location} onChange={(e) => setFuelForm({ ...fuelForm, location: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Notes</Label><Textarea value={fuelForm.notes} onChange={(e) => setFuelForm({ ...fuelForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowFuel(false)}>Cancel</Button><Button onClick={handleAddFuel} disabled={saving} data-testid="save-fuel-btn">{saving ? 'Saving...' : 'Log Entry'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MILEAGE DIALOG */}
      <Dialog open={showMileage} onOpenChange={setShowMileage}>
        <DialogContent data-testid="mileage-dialog">
          <DialogHeader><DialogTitle>Log Mileage: {selectedVehicle?.year} {selectedVehicle?.make} {selectedVehicle?.model}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Month</Label><Select value={String(mileageForm.month)} onValueChange={(v) => setMileageForm({ ...mileageForm, month: parseInt(v) })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <SelectItem key={m} value={String(m)}>{new Date(2000, m-1).toLocaleString('default', {month:'long'})}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label>Year</Label><Input type="number" value={mileageForm.year} onChange={(e) => setMileageForm({ ...mileageForm, year: parseInt(e.target.value) })} /></div>
            </div>
            <div className="space-y-1"><Label>Total Miles for Month *</Label><Input type="number" value={mileageForm.total_mileage} onChange={(e) => setMileageForm({ ...mileageForm, total_mileage: e.target.value })} data-testid="mileage-total" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowMileage(false)}>Cancel</Button><Button onClick={handleAddMileage} disabled={saving} data-testid="save-mileage-btn">{saving ? 'Saving...' : 'Save'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {/* MILEAGE AUDIT DIALOG */}
      <Dialog open={showMileageAudit} onOpenChange={setShowMileageAudit}>
        <DialogContent className="max-w-2xl" data-testid="mileage-audit-dialog">
          <DialogHeader><DialogTitle>Mileage Edit History</DialogTitle><DialogDescription>{selectedVehicle?.year} {selectedVehicle?.make} {selectedVehicle?.model}</DialogDescription></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {mileageAuditLog.length > 0 ? (
              <Table><TableHeader><TableRow><TableHead>Period</TableHead><TableHead className="text-right">Old Value</TableHead><TableHead className="text-right">New Value</TableHead><TableHead>Changed By</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                <TableBody>{mileageAuditLog.map((log, i) => (
                  <TableRow key={i}>
                    <TableCell>{log.month}/{log.year}</TableCell>
                    <TableCell className="text-right">{log.old_value != null ? log.old_value.toLocaleString() : <span className="text-slate-400">New</span>}</TableCell>
                    <TableCell className="text-right font-medium">{log.new_value?.toLocaleString()}</TableCell>
                    <TableCell>{log.changed_by_name}</TableCell>
                    <TableCell className="text-sm">{formatDate(log.created_at)}</TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            ) : <p className="text-center py-8 text-slate-400">No edit history yet</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* DUPLICATE IMPORT PROMPT */}
      <Dialog open={!!showDuplicatePrompt} onOpenChange={() => { setShowDuplicatePrompt(null); setPendingFile(null); }}>
        <DialogContent data-testid="duplicate-import-dialog">
          <DialogHeader><DialogTitle>Import Already Exists</DialogTitle></DialogHeader>
          <p className="text-sm py-2">{showDuplicatePrompt?.message}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowDuplicatePrompt(null); setPendingFile(null); }}>Cancel</Button>
            <Button variant="outline" onClick={() => { if (pendingFile) handleWexImport(pendingFile, 'merge'); setShowDuplicatePrompt(null); }} data-testid="import-merge-btn">Merge (Add to Existing)</Button>
            <Button variant="destructive" onClick={() => { if (pendingFile) handleWexImport(pendingFile, 'replace'); setShowDuplicatePrompt(null); }} data-testid="import-replace-btn">Replace Existing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ASSIGN UNMATCHED TRANSACTION DIALOG */}
      <Dialog open={!!showAssignDialog} onOpenChange={() => setShowAssignDialog(null)}>
        <DialogContent data-testid="assign-tx-dialog">
          <DialogHeader><DialogTitle>Assign Transaction to Vehicle</DialogTitle><DialogDescription>Card: {showAssignDialog?.card_number} | {showAssignDialog?.driver_name} | {showAssignDialog?.gallons?.toFixed(2)} gal | ${showAssignDialog?.total_cost?.toFixed(2)}</DialogDescription></DialogHeader>
          <div className="py-2">
            <Label>Select Vehicle</Label>
            <Select onValueChange={(vid) => handleAssignUnmatched(showAssignDialog.id, vid)}>
              <SelectTrigger data-testid="assign-vehicle-select"><SelectValue placeholder="Choose a vehicle..." /></SelectTrigger>
              <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model} — {v.gas_card_number || 'No card'}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Fleet-wide fuel chart component
function FleetWideChart() {
  const chart = useChartTheme();
  const [data, setData] = useState([]);
  useEffect(() => {
    api.get('/fleet/charts/fuel').then(r => setData(r.data)).catch(() => {});
  }, []);
  if (data.length < 2) return <p className="text-sm text-slate-400 py-8 text-center">Not enough data for charts yet. Add fuel entries to see trends.</p>;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
        <XAxis dataKey="month" stroke={chart.axis} tick={{ fill: chart.axis, fontSize: 12 }} />
        <YAxis stroke={chart.axis} tick={{ fill: chart.axis, fontSize: 12 }} />
        <Tooltip contentStyle={{ backgroundColor: chart.surface, border: `1px solid ${chart.border}`, borderRadius: 6, color: chart.text }} labelStyle={{ color: chart.text }} itemStyle={{ color: chart.text }} />
        <Legend wrapperStyle={{ color: chart.axis }} />
        <Bar dataKey="gallons" fill={chart.series[0]} name="Gallons" />
        <Bar dataKey="cost" fill={chart.series[4]} name="Cost ($)" />
      </BarChart>
    </ResponsiveContainer>
  );
}
